import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { Formik, type FormikTouched } from "formik";
import React, { type FC, useState } from "react";
import { array, object, string } from "yup";

import { type Beneficiary, type BeneficiaryRequest, type PensionBeneficiary, ProductType } from "@repo/api";
import { cs } from "@repo/ui";
import { usePrimaryAPIClient } from "../../../context/PrimaryAPIClient";
import { useDrawer } from "../../Drawer/context";
import FormMultiStepDrawer from "../../Drawer/FormMultiStepDrawer";
import {
    mapFetchApiErrorToSubmitStatus,
    SubmitStatus,
    SubmitStatusStep,
} from "../../Drawer/GlobalSteps/SubmitStatusStep";
import { beneficiariesSchema, ratiosSchema } from "../../Form/rules";
import BeneficiariesList from "../steps/BeneficiariesList";
import BeneficiarInfo from "../steps/BeneficiarInfo";
import ChangesOverview from "../steps/ChangesOverview";
import ShareDistribution from "../steps/ShareDistribution";
import { beneficiariesInitialValues, cleanAddress, ratiosInitialValues } from "./cleaners";

type BeneficiariesChangeFormProps = {
    id: string;
    contractType: string;
    beneficiaries: Beneficiary[] | PensionBeneficiary[];
    onSuccess: () => Promise<void>;
    onError: () => Promise<void>;
};

export type BeneficiariesChangeFormFormValues = {
    beneficiaries: (Beneficiary | PensionBeneficiary)[];
    editedBeneficiar: number;
    ratios: {
        ratio: number | null | undefined;
    }[];
};

const BeneficiariesChangeForm: FC<BeneficiariesChangeFormProps> = ({
    id,
    contractType,
    beneficiaries: initialBeneficiaries,
    onSuccess,
    onError,
}) => {
    const { setStep, step } = useDrawer();
    const { contractsPpApi, contractsDpsApi, contractsLifeApi } = usePrimaryAPIClient();
    const ai = useAppInsightsContext();

    const beneficiariesChange = (newSetup: unknown): void => {
        ai.trackEvent({
            name: "Changed beneficiaries [submit]",
            properties: {
                previous: initialBeneficiaries,
                new: newSetup,
                contractId: id,
            },
        });
    };

    const initialValues: BeneficiariesChangeFormFormValues = {
        beneficiaries: beneficiariesInitialValues(initialBeneficiaries),
        editedBeneficiar: initialBeneficiaries.length,
        ratios: ratiosInitialValues(initialBeneficiaries),
    };

    const validationSchema = object().shape({
        beneficiaries: beneficiariesSchema,
        ratios: step === 2 ? ratiosSchema : array(),
        valuesUnchanged:
            step === 2 || initialBeneficiaries.length === 0 // when the initial state is 0 beneficiaries, show error message instantly on the beneficiarList step because there is no ratio distribution step in this situation
                ? string().parentHasChanges({ ...initialValues })
                : string(),
    });

    const [removedBeneficiaries, setRemovedBeneficiaries] = useState([]);

    return (
        <Formik<BeneficiariesChangeFormFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setStatus }) => {
                setStatus(SubmitStatus.Submitting);
                const beneficiaries = values.beneficiaries.map((item, index) => {
                    const address = {
                        // don't check if country is empty, there doesn't exist an input to control this value anymore, country code is posted programatically after submit and not taken from formik initial values
                        ...item.address,
                        countryName: undefined,
                        country: undefined,
                        type: undefined,
                    };
                    const cleanedAddress = cleanAddress(address);
                    const completedInfo = {
                        ...item,
                        ...values.ratios[index],
                        address:
                            Object.keys(cleanedAddress).length === 0
                                ? null
                                : { ...cleanedAddress, type: "Delivery", country: "CZ" },
                    };
                    return completedInfo;
                });
                try {
                    if (contractType === ProductType.Pf) {
                        await contractsPpApi.setPPBeneficiariesPut({
                            contractId: id,
                            setPensionBeneficiariesRequest: {
                                beneficiaries: [...beneficiaries, ...removedBeneficiaries] as PensionBeneficiary[],
                            },
                        });
                        beneficiariesChange(values.beneficiaries);
                    }
                    if (contractType === ProductType.Uf) {
                        await contractsDpsApi.setDPSBeneficiariesPut({
                            contractId: id,
                            setPensionBeneficiariesRequest: {
                                beneficiaries: [...beneficiaries, ...removedBeneficiaries] as PensionBeneficiary[],
                            },
                        });
                        beneficiariesChange(values.beneficiaries);
                    }
                    if (contractType === ProductType.Cl) {
                        await contractsLifeApi.setLifeBeneficiariesPut({
                            contractId: id,
                            setBeneficiariesRequest: {
                                beneficiaries: beneficiaries as BeneficiaryRequest[],
                            },
                        });
                        beneficiariesChange(values.beneficiaries);
                    }
                    setStatus(SubmitStatus.Success);
                    await onSuccess();
                } catch (error) {
                    setStatus(mapFetchApiErrorToSubmitStatus(error));
                    await onError();
                }
            }}
        >
            {({ setTouched, setFieldTouched, validateForm, values, submitForm, status, setSubmitting, isSubmitting }) => {
                const touchEditedBeneficiar = (prefix: string, errors: Record<string, unknown> | string): void => {
                    if (typeof errors === "string") {
                        setFieldTouched(prefix);
                    } else {
                        Object.keys(errors).forEach((key) => {
                            const name = `${prefix}${prefix !== "" ? "." : ""}${key}`;
                            if (
                                typeof errors[key] === "object" &&
                                errors[key] !== null &&
                                !Array.isArray(errors[key])
                            ) {
                                touchEditedBeneficiar(name, errors[key] as Record<string, unknown>);
                            } else {
                                setFieldTouched(name);
                            }
                        });
                    }
                };
                return (
                    <FormMultiStepDrawer
                        title={
                            contractType &&
                            (contractType === ProductType.Cl ||
                                contractType === ProductType.Clf ||
                                contractType === ProductType.Uf ||
                                contractType === ProductType.Pf)
                                ? cs.product.beneficiaries.changeBeneficiaries[contractType]
                                : cs.product.beneficiaries.changeBeneficiaries.default
                        }
                        validateForm={validateForm}
                        setFieldTouched={setFieldTouched}
                        setSubmitting={setSubmitting}
                        isSubmitting={isSubmitting}
                        formStatus={status}
                    >
                        {{
                            component: (
                                <BeneficiariesList
                                    contractType={contractType}
                                    removedBeneficiaries={removedBeneficiaries}
                                    setRemovedBeneficiaries={setRemovedBeneficiaries}
                                />
                            ),
                            stepConfig: {
                                label:
                                    values.beneficiaries.length === 0
                                        ? cs.global.confirmChange
                                        : cs.beneficiariesChange.beneficiariesListContinue,
                                customFunction: () => {
                                    validateForm()
                                        .then((errors) => {
                                            setTouched({
                                                ...errors,
                                            } as FormikTouched<BeneficiariesChangeFormFormValues>); // touch all fields with error
                                            if (Object.keys(errors).length === 0) {
                                                if (values.beneficiaries.length === 0) {
                                                    setStep(3);
                                                } else {
                                                    setStep(2);
                                                }
                                            }
                                        })
                                        .catch((err: unknown) => {
                                            throw err;
                                        });
                                },
                            },
                            stepName: "BeneficiariesList",
                        }}
                        {{
                            component: <BeneficiarInfo />,
                            stepConfig: {
                                title:
                                    contractType &&
                                    (contractType === ProductType.Cl ||
                                        contractType === ProductType.Uf ||
                                        contractType === ProductType.Pf)
                                        ? cs.product.beneficiaries.changeBeneficiarTitle[contractType]
                                        : cs.product.beneficiaries.changeBeneficiarTitle.default,
                                label:
                                    contractType &&
                                    (contractType === ProductType.Cl ||
                                        contractType === ProductType.Uf ||
                                        contractType === ProductType.Pf)
                                        ? cs.product.beneficiaries.backBeneficiaries[contractType]
                                        : cs.product.beneficiaries.backBeneficiaries.default,
                                customFunction: () => {
                                    validateForm()
                                        .then((errors) => {
                                            if (errors.beneficiaries?.[values.editedBeneficiar]) {
                                                touchEditedBeneficiar(
                                                    `beneficiaries.${String(values.editedBeneficiar)}`,
                                                    errors.beneficiaries[values.editedBeneficiar],
                                                );
                                            }
                                            const stepErrors = errors.beneficiaries?.[values.editedBeneficiar] ?? {};
                                            if (Object.keys(stepErrors).length === 0) {
                                                setStep(0);
                                            }
                                        })
                                        .catch((err: unknown) => {
                                            throw err;
                                        });
                                },
                                customBackFunction: () => {
                                    validateForm()
                                        .then((errors) => {
                                            if (errors.beneficiaries?.[values.editedBeneficiar]) {
                                                touchEditedBeneficiar(
                                                    `beneficiaries.${String(values.editedBeneficiar)}`,
                                                    errors.beneficiaries[values.editedBeneficiar],
                                                );
                                            }
                                        })
                                        .catch((err: unknown) => {
                                            throw err;
                                        });
                                    return true;
                                },
                            },
                            stepName: "BeneficiarInfo",
                        }}
                        {{
                            component: <ShareDistribution />,
                            stepConfig: {
                                label: cs.beneficiariesChange.shareDistributionContinue,
                                customFunction: () => {
                                    validateForm()
                                        .then((errors) => {
                                            setTouched({
                                                ...errors,
                                            } as FormikTouched<BeneficiariesChangeFormFormValues>); // touch all fields with error
                                            if (Object.keys(errors).length === 0) {
                                                setStep(3);
                                            }
                                        })
                                        .catch((err: unknown) => {
                                            throw err;
                                        });
                                },
                            },
                            stepName: "ShareDistribution",
                        }}
                        {{
                            component: <ChangesOverview />,
                            stepConfig: {
                                label: cs.global.confirmChange,
                                customFunction: () => {
                                    void submitForm();
                                    setStep(4);
                                },
                            },
                            stepName: "ChangesOverview",
                        }}

                        {{
                            component: <SubmitStatusStep status={status as SubmitStatus} />,
                            stepName: "SubmitStatusStep",
                        }}
                    </FormMultiStepDrawer>
                );
            }}
        </Formik>
    );
};

export default BeneficiariesChangeForm;
